/*>
* @license
* Copyright (c) 2014-2022 ABILITY Network, Inc., an INOVALON, Inc. company. All Rights Reserved.
* 
* This computer program is CONFIDENTIAL and a TRADE SECRET of ABILITY Network, Inc.
* The receipt or possession of this program does not convey any rights to use,
* reproduce or disclose its contents in whole or in part, without the specific
* written consent of ABILITY Network, Inc. Any use, reproduction or disclosure of
* this program without the express written consent of ABILITY Network, Inc. is a
* violation of the copyright laws and may subject you to criminal prosecution.
<*/
import { BaseSettingsManager } from '../configuration/UserSessionSettingsManager'
import { IContentApiClient } from '../services/ContentApiClient/contracts'
import { ContentApiClient } from '../services/ContentApiClient/ContentApiClient'
import { MockContentApiClient } from '../services/ContentApiClient/MockContentApiClient'
import { IContentStateManager } from '../services/ContentStateManager/contracts'
import { ContentStateManager } from '../services/ContentStateManager/ContentStateManager'
import { container } from './ServiceLocator'
import { ISectionStateManager } from '../services/SectionStateManager/contracts'
import { SectionStateManager } from '../services/SectionStateManager/SectionStateManager'
import ISectionApiClient from '../services/SectionApiClient/contracts'
import { MockSectionApiClient } from '../services/SectionApiClient/MockSectionApiClient'
import { SectionApiClient } from '../services/SectionApiClient/SectionApiClient'
import ISearchResultsApiClient from '../services/SearchResultsApiClient/contracts'
import { MockSearchResultsApiClient } from '../services/SearchResultsApiClient/MockSearchResultsApiClient'
import { ISearchResultsStateManager } from '../services/SearchResultsStateManager/contracts'
import { SearchResultsStateManager } from '../services/SearchResultsStateManager/SearchResultsStateManager'
import { SearchResultsApiClient } from '../services/SearchResultsApiClient/SearchResultsApiClient'
import { IApiClient } from '../services/ApiClient/IApiClient'
import { ApiClient } from '../services/ApiClient/ApiClient'
import IProductsApiClient from '../services/ProductsApiClient/contracts'
import { MockProductsApiClient } from '../services/ProductsApiClient/MockProductsApiClient'
import { IProductContentStateManager } from '../services/ProductContentStateManager/contracts'
import { ProductContentStateManager } from '../services/ProductContentStateManager/ProductContentStateManager'
import { IProductStateManager } from '../services/ProductStateManager/contracts'
import { ProductStateManager } from '../services/ProductStateManager/ProductStateManager'
import { ProductsApiClient } from '../services/ProductsApiClient/ProductsApiClient'
import ILandingPageApiClient from '../services/LandingPageApiClient/contracts'
import { MockLandingPageApiClient } from '../services/LandingPageApiClient/MockLandingPageApiClient'
import { ILandingPageStateManager } from '../services/LandingPageStateManager/contracts'
import { LandingPageStateManager } from '../services/LandingPageStateManager/LandingPageStateManager'
import { LandingPageApiClient } from '../services/LandingPageApiClient/LandingPageApiClient'

/**
 * This module centralizes all configurable application settings into a single public object with documentation
 * Settings are loaded on demand through strongly-typed properties
 * Settings can be configured at build-time via use of .env files or at startup-time via appConfig.js
 */

/**
 * the class for the singleton SettingsManager instance
 *
 * @class SettingsManager
 */
export class SettingsManager extends BaseSettingsManager {
	static REACT_APP_SSO_TOKEN_NAME = 'supportsite.ssoToken'
	static REACT_APP_Refresh_TOKEN_NAME = 'supportsite.refreshToken'
	static REACT_APP_ProductCategories_TOKEN_NAME = 'productcategoriesToken'
	/**
	 * register all injectables to an ioc container.
	 * this should be called once and only once per container at the time of application bootstrap
	 *
	 * @export
	 * @param {Container} iocContainer the container to configure
	 */
	static configureContainer = (): void => {
		// note: there doesnt (yet) seem to be a mature/stable auto-wiring for services.
		// until one is available we will bind services explicitly in this method
		BaseSettingsManager.configureContainer()

		if (BaseSettingsManager.REACT_APP_IS_MAIN_API_MOCKED().value) {
			container
				.bind<IApiClient>('IApiClient')
				.to(ApiClient)
				.inSingletonScope()

			container
				.bind<ISectionApiClient>('ISectionApiClient')
				.to(MockSectionApiClient)
				.inSingletonScope()

			container
				.bind<ISectionStateManager>('ISectionStateManager')
				.to(SectionStateManager)
				.inSingletonScope()

			container
				.bind<ISearchResultsApiClient>('ISearchResultsApiClient')
				.to(MockSearchResultsApiClient)
				.inSingletonScope()

			container
				.bind<ISearchResultsStateManager>('ISearchResultsStateManager')
				.to(SearchResultsStateManager)
				.inSingletonScope()
			container
				.bind<IProductsApiClient>('IProductsApiClient')
				.to(MockProductsApiClient)
				.inSingletonScope()
			container
				.bind<IProductContentStateManager>('IProductContentStateManager')
				.to(ProductContentStateManager)
				.inSingletonScope()
			container
				.bind<IProductStateManager>('IProductStateManager')
				.to(ProductStateManager)
				.inSingletonScope()
			container
				.bind<ILandingPageApiClient>('ILandingPageApiClient')
				.to(MockLandingPageApiClient)
				.inSingletonScope()
			container
				.bind<ILandingPageStateManager>('ILandingPageStateManager')
				.to(LandingPageStateManager)
				.inSingletonScope()
			container
				.bind<IContentApiClient>('IContentApiClient')
				.to(MockContentApiClient)
				.inSingletonScope()
			container
				.bind<IContentStateManager>('IContentStateManager')
				.to(ContentStateManager)
				.inSingletonScope()
		} else {
			container
				.bind<IApiClient>('IApiClient')
				.to(ApiClient)
				.inSingletonScope()

			container
				.bind<ISectionApiClient>('ISectionApiClient')
				.to(SectionApiClient)
				.inSingletonScope()

			container
				.bind<ISectionStateManager>('ISectionStateManager')
				.to(SectionStateManager)
				.inSingletonScope()

			container
				.bind<ISearchResultsApiClient>('ISearchResultsApiClient')
				.to(SearchResultsApiClient)
				.inSingletonScope()

			container
				.bind<ISearchResultsStateManager>('ISearchResultsStateManager')
				.to(SearchResultsStateManager)
				.inSingletonScope()
			container
				.bind<ILandingPageApiClient>('ILandingPageApiClient')
				.to(LandingPageApiClient)
				.inSingletonScope()

			container
				.bind<ILandingPageStateManager>('ILandingPageStateManager')
				.to(LandingPageStateManager)
				.inSingletonScope()

			container
				.bind<IContentApiClient>('IContentApiClient')
				.to(ContentApiClient)
				.inSingletonScope()

			container
				.bind<IContentStateManager>('IContentStateManager')
				.to(ContentStateManager)
				.inSingletonScope()
			container
				.bind<IProductsApiClient>('IProductsApiClient')
				.to(ProductsApiClient)
				.inSingletonScope()

			container
				.bind<IProductContentStateManager>('IProductContentStateManager')
				.to(ProductContentStateManager)
				.inSingletonScope()

			container
				.bind<IProductStateManager>('IProductStateManager')
				.to(ProductStateManager)
				.inSingletonScope()
		}
	}
}
